<template>
  <div class="m-product-filter m-product-filter--mobile">
    <div class="m-product-filter__filters">
      <div class="m-product-filter__accordions">
        <accordion
          v-for="(filter, filterIndex) in filters"
          :key="filterIndex"
          :title="$t('FacetGroup_' + filter.name, 'FacetGroup_' + filter.name)"
          :icon-right="true"
          :hide-content-desktop="true"
          :is-filter="true"
          class="m-product-filter__accordion"
          :class="filterIndex > 3 && !showAllFilters ? 'd-lg-none' : ''"
        >
          <div>
            <div
              v-if="$isNotNullOrUndefined(filter.type) && filter.type == 'range' && filter.minValue !== filter.maxValue && getRange(filter)"
              class="m-product-filter__item -range"
            >
              <div>
                <base-input
                  v-model="getRange(filter).range[0]"
                  :placeholder="$t('From')"
                  :label="$t('From')"
                  @click="selectValue"
                  @blur="(event) => updateSliderValue(filterIndex, event, 'minValue')"
                  @keyup.enter="(event) => updateSliderValue(filterIndex, event, 'minValue')"
                />
                <span class="small font-weight-light">{{ $t('Min') }}: {{ filter.minValue }}</span>
              </div>
              <div>
                <base-input
                  v-model="getRange(filter).range[1]"
                  :placeholder="$t('To')"
                  :label="$t('To')"
                  @click="selectValue"
                  @blur="(event) => updateSliderValue(filterIndex, event, 'maxValue')"
                  @keyup.enter="(event) => updateSliderValue(filterIndex, event, 'maxValue')"
                />
                <span class="small font-weight-light">{{ $t('Max') }}: {{ filter.maxValue }}</span>
              </div>
            </div>
            <div v-else>
              <div v-if="getFilterArray(filter) && (getFilterArray(filter).length > 5 || filterSearchQueries[filter.name])">
                <base-input
                  v-model="filterSearchQueries[filter.name]"
                  class="m-product-filter__search"
                  :placeholder="$t('SearchInFilters', 'Søg i filtre')"
                  @input="handleFilterSearch(filter.filters, filter.name)"
                />
              </div>
              <div
                class="m-product-filter__items"
                :class="{
                  '-is-expanded': isExpanded(filterIndex),
                  'm-product-filter__expanded': !(
                    getFilterArray(filter) &&
                    (getFilterArray(filter).length > 5 || filterSearchQueries[filter.name])
                  )
                }"
              >
                <div
                  v-for="(filterValue, i) in getFilterArray(filter)"
                  :key="filterValue.key"
                  class="m-product-filter__item"
                  :class="{ 'd-none': !isExpanded(filterIndex) && i >= visibleCheckBoxes }"
                >
                  <base-checkbox
                    v-model="filterModel"
                    :name="`${filterValue.name}`"
                    :value="(filterValue.group || filter.name) + '_' + filterValue.value"
                    :label="`${filterValue.name}`"
                    :required="false"
                    :disabled="false"
                    class="mb-0 w-100"
                    @change="filterChanged(filterValue.group || filter.name, filterValue.value, null)"
                  >
                    <div slot="label" class="a-checkbox__label">
                      {{ filterValue.name }}
                      <span v-if="filterValue.count" class="m-product-filter__count">({{ filterValue.count }})</span>
                    </div>
                  </base-checkbox>
                </div>
              </div>
            </div>
            <div class="m-product-filter__accordion-controls">
              <button
                class="a-button -outline-primary -small"
                @click="toggle($t('FacetGroup_' + filter.name, 'FacetGroup_' + filter.name))"
              >
                {{ $t('Close', 'Luk') }}
              </button>
            </div>
          </div>
        </accordion>
      </div>
      <div class="m-product-filter__controls d-none d-lg-block">
        <base-button v-if="filters.length > 4" class="a-button -primary" @click="showAllFilters = !showAllFilters">
          <base-icon :icon="showAllFilters ? 'close' : 'filter'" />
          <span>
            {{ $t('ShowFilters', 'Alle filtre') }}
          </span>
        </base-button>
      </div>
    </div>

    <div v-if="selectedFilters.length" class="m-product-filter__selected-filters">
      <span v-for="(selectedFilter, index) in selectedFilters" :key="index" class="m-product-filter__selected-filters-group">
        <template v-if="selectedFilter.values.from && selectedFilter.values.to">
          <base-button
            class="m-product-filter__selected-filters-button"
            @click="
              removeFilter(
                selectedFilter.id,
                [selectedFilter.values.from, selectedFilter.values.to],
                selectedFilter.type,
                selectedFilter.customId
              )
            "
          >
            {{ $t('FacetGroup_' + selectedFilter.id, 'FacetGroup_' + selectedFilter.id) }}: {{ selectedFilter.values.from }} -
            {{ selectedFilter.values.to }}
            <base-icon icon="close" />
          </base-button>
        </template>

        <template v-else-if="selectedFilter.id !== 'smartsearch' && selectedFilter.id !== 'categories' && selectedFilter.values">
          <base-button
            v-for="(value, i) in selectedFilter.values"
            :key="i"
            class="m-product-filter__selected-filters-button"
            @click="removeFilter(selectedFilter.id, value, selectedFilter.type, selectedFilter.customId)"
          >
            {{ value }}
            <base-icon icon="close" />
          </base-button>
        </template>
      </span>

      <base-button v-if="showResetButton" class="m-product-filter__selected-filters-reset" @click="clearFilter">
        {{ $t('Reset', 'Ryd alle') }}
      </base-button>
    </div>
  </div>
</template>

<script>
import BaseCheckbox from '@/components/atoms/BaseCheckbox'
import Accordion from '@/components/molecules/Accordion'
import BaseIcon from '@/components/atoms/BaseIcon'
import BaseButton from '@/components/atoms/BaseButton'
import BaseInput from '@/components/atoms/BaseInput'
import debounce from 'debounce'
import { updateQueryStringParam } from '@/utils/query.js'
import 'vue-slider-component/dist-css/vue-slider-component.css'
import filterListRemapHelper from '@/utils/ShopCommerce/filter-list-remap-helper.js'
import LoginMixin from '@/mixins/loginMixin'
import { trackFilterCategories, trackFilters } from '@/utils/enhancedEcomTracking'

export default {
  name: 'ProductFilter',
  components: {
    BaseCheckbox,
    Accordion,
    BaseIcon,
    BaseButton,
    BaseInput
  },
  mixins: [LoginMixin],
  props: {
    facets: {
      type: Array,
      default: () => []
    },
    isMobileFilter: {
      type: Boolean,
      default: false
    },
    hideCustomFilters: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      open: [],
      filterModel: [],
      selectedFilters: [],
      expanded: [],
      selectedCheckboxes: [],
      visibleCheckBoxes: 5,
      filterSearchQueries: {},
      filteredFilterGroups: {},
      showAllFilters: false
    }
  },
  computed: {
    showResetButton() {
      if (!this.selectedFilters.length) {
        return false
      }

      const hasOnlyCategories = this.selectedFilters.every((filter) => filter.id === 'categories')
      const hasOnlySmartsearch = this.selectedFilters.every((filter) => filter.id === 'smartsearch')
      const hasOnlyCategoriesAndSmartsearch = this.selectedFilters.every(
        (filter) => filter.id === 'categories' || filter.id === 'smartsearch'
      )

      return !(hasOnlyCategories || hasOnlySmartsearch || hasOnlyCategoriesAndSmartsearch)
    },
    filters() {
      let filters = filterListRemapHelper(this.facets).filter((filter) => filter.name?.toLowerCase() !== 'assortments')
      return [this.customFilters, ...filters].filter(
        (filter) => filter.filters?.length || (filter.type == 'range' && filter.minValue !== filter.maxValue)
      )
    },
    assortmentFilters() {
      let assortments = filterListRemapHelper(this.facets).find((filter) => filter.name?.toLowerCase() === 'assortments')
      return (assortments?.filters ?? []).map((filterValue) => ({ ...filterValue, group: 'assortments' }))
    },
    customFilters() {
      return {
        name: 'custom',
        filters: [
          ...this.assortmentFilters,
          {
            count: 0,
            name: this.$t('FilterCustomBronnumCare', 'Brønnum Care'),
            selected: false,
            value: 'broennum_care'
          },
          !this.hideCustomFilters
            ? {
                count: 0,
                name: this.$t('ProductCard_Discount_LAVPRIS', 'Fast Lavpris'),
                selected: false,
                value: 'fast_lavpris'
              }
            : {},
          !this.hideCustomFilters
            ? {
                count: 0,
                name: this.$t('ProductCard_Discount_RESTSALG', 'Restsalg'),
                selected: false,
                value: 'restsalg'
              }
            : {},
          !this.hideCustomFilters
            ? {
                count: 0,
                name: this.$t('ProductCard_Discount_Badge', 'Tilbud'),
                selected: false,
                value: 'onsale'
              }
            : {},
          !this.hideCustomFilters
            ? {
                count: 0,
                name: this.$t('FilterCustomStockItems', 'FilterCustomStockItems'),
                selected: false,
                value: 'orderingitem'
              }
            : {},
          this.isLoggedIn && this.latestBoughtProductsList.length
            ? {
                count: 0,
                name: this.$t('FilterCustomLatestBought', 'Tidligere køb'),
                selected: false,
                value: 'bought'
              }
            : {},
          this.isLoggedIn && this.favorites.length
            ? {
                count: 0,
                name: this.$t('FilterCustomFavorites', 'Favoritter'),
                selected: false,
                value: 'favorites'
              }
            : {}
        ].filter((facet) => Object.keys(facet).length)
      }
    },
    favorites() {
      return this.$store.getters['user/user/getFavoriteListSkus']
    },
    latestBoughtProductsList() {
      return this.$store.getters['user/user/getLatestBoughtProductsList']
    },
    currentCulture() {
      return this.$store.getters['site/getCurrentCulture']
    },
    isLoggedIn() {
      return this.$store.getters['user/user/isLoggedIn']
    }
  },
  created() {
    this.$store.commit('products/productFilter/resetSelectedFilter')
    this.setFilterFromQuery()
    this.selectedFilters = this.$store.getters['products/productFilter/getSelectedFilters']
    this.filterChanged = debounce(this.filterChanged, 500)
  },
  async mounted() {
    if (this.isLoggedIn) {
      if (this.favorites || this.favorites.length === 0) {
        await this.$store.dispatch('user/user/fetchFavoriteListSkus')
      }

      this.$store.dispatch('user/user/fetchLatestBoughtProductsList')
    }
  },
  methods: {
    setFilterFromQuery() {
      let query = Object.keys(this.$route.query)
      let filterModelArray = []
      let openAccordionArray = []

      if (query.length !== 0) {
        query.forEach((facet) => {
          let facetObj = this.facets.find((x) => x.name === facet) // Find the facet by name
          if (this.$isNotNullOrUndefined(facetObj)) {
            if (facetObj.min && facetObj.max) {
              const filterRange = {
                to: this.$route.query[facet].split(',')[1],
                from: this.$route.query[facet].split(',')[0]
              }

              let filterObject = {
                id: facet,
                type: 'range',
                values: filterRange
              }

              filterModelArray.push({
                facet,
                type: 'range',
                range: [parseInt(filterRange.from), parseInt(filterRange.to)]
              })

              this.selectedFilters.push(filterObject)
            } else {
              this.$route.query[facet].split('|').forEach((facetValue) => {
                filterModelArray.push(facet + '_' + facetValue)
              })

              this.selectedFilters.push({
                id: facet,
                values: this.$route.query[facet].split('|')
              })
            }
            openAccordionArray.push(facet)
          } else {
            filterModelArray.push(`custom_${facet}`)
            let customFilter = this.getCustomSelectedFilter(facet)
            if (customFilter) {
              this.selectedFilters.push(customFilter)
            }
          }
        })

        this.filterModel = filterModelArray
        this.open = openAccordionArray
        this.$store.commit('products/productFilter/setSelectedFilters', this.selectedFilters)
        this.$emit('filterChanged', this.selectedFilters)
      }
    },
    getCustomSelectedFilter(name) {
      let custom = {
        bought: {
          id: 'id',
          customId: 'bought',
          values: this.latestBoughtProductsList
        },
        favorites: {
          id: 'id',
          customId: 'favorites',
          values: this.favorites
        },
        broennum_care: {
          id: 'broennum_care',
          customId: 'broennum_care',
          values: ['Ja']
        },
        fast_lavpris: {
          id: 'advertisingcode',
          customId: 'fast_lavpris',
          values: ['LAVPRIS']
        },
        restsalg: {
          id: 'advertisingcode',
          customId: 'restsalg',
          values: ['RESTSALG']
        },
        onsale: {
          id: 'onsale',
          customId: 'onsale',
          values: ['True']
        },
        orderingitem: {
          id: 'orderingitem',
          customId: 'orderingitem',
          values: ['FALSE']
        },
        deliverytimeoutofstock: {
          id: 'deliverytimeoutofstock',
          customId: 'deliverytimeoutofstock',
          values: ''
        }
      }
      return custom[name]
    },
    toggle(payload) {
      if (this.open.includes(payload)) {
        this.open = this.open.filter((item) => item !== payload)
      } else {
        this.open = [payload]
      }
    },
    expand(payload) {
      if (this.expanded.includes(payload)) {
        this.expanded = this.expanded.filter((item) => item !== payload)
      } else {
        this.expanded.push(payload)
      }
    },
    isExpanded(payload) {
      return true
    },

    removeFilter(filterKey, filterValue, type, customId, rangeMinValue, rangeMaxValue) {
      const filterModelTestString = filterKey + '_' + filterValue

      // range filters
      if (type === 'range') {
        this.updateSliderValue(
          this.filters.findIndex((x) => x.name === filterKey),
          null,
          'reset'
        )
        // custom filters
      } else if (!this.$isNotNullOrUndefined(this.facets?.key === customId)) {
        this.filterModel = this.filterModel.filter((x) => x !== `custom_${customId}`)
        this.filterChanged(filterKey, filterValue, type, rangeMinValue, rangeMaxValue)
        // facets
      } else {
        this.filterModel = this.filterModel.filter((x) => x !== filterModelTestString)
        this.filterChanged(filterKey, filterValue, type, rangeMinValue, rangeMaxValue)
      }
    },

    filterChanged(filterKey, filterValue, type, rangeMinValue, rangeMaxValue) {
      // gtm tracking filters
      this.gtmFilters(filterKey, filterValue)

      // Determine if the filter group is in the array and if so, get the index
      let filterIndex = this.selectedFilters.findIndex((x) => x.id === filterKey || x.customId === filterValue)
      let filterExists = filterIndex !== -1

      if (filterExists) {
        // Determine if the filter value is in the array and if so, get the index
        let selectedFilterIndex = Object.values(this.selectedFilters[filterIndex]?.values).findIndex((x) => x === filterValue)
        let selectedFilterExists = selectedFilterIndex !== -1

        if (selectedFilterExists) {
          // If the value exist - we remove it
          this.selectedFilters[filterIndex].values.splice(selectedFilterIndex, 1)
          // If the filterGroup has no filterValues, remove the filterGroup
          let noFilterValues = this.selectedFilters[filterIndex].values.length === 0
          if (noFilterValues) {
            // Remove empty filterGroup from queryString
            updateQueryStringParam(filterKey, '')
            this.selectedFilters.splice(filterIndex, 1)
          }
        } else if (this.selectedFilters[filterIndex].customId) {
          updateQueryStringParam(this.selectedFilters[filterIndex].customId, '')
          this.selectedFilters.splice(filterIndex, 1)
        } else {
          // If the value does not exist we push it to the filterGroup
          if (type === 'range' && filterValue && filterValue[0] && filterValue[1]) {
            const filterRange = {
              to: filterValue[1],
              from: filterValue[0]
            }
            // if diameter values are default min and max, then delete filter
            if (filterRange.to === rangeMaxValue && filterRange.from === rangeMinValue) {
              updateQueryStringParam(filterKey, '')
              this.selectedFilters.splice(filterIndex, 1)
            } else {
              this.selectedFilters[filterIndex].values = filterRange
            }
          } else {
            this.selectedFilters[filterIndex].values?.push(filterValue)
          }
        }
      } else {
        if (type === 'range' && filterValue && filterValue[0] && filterValue[1]) {
          const filterRange = {
            to: filterValue[1],
            from: filterValue[0]
          }

          let filterObject = {
            id: filterKey,
            type: 'range',
            values: filterRange
          }
          // add filter ranges to the filterModel array
          this.filterModel.push({ filterKey, type: 'range', range: [parseInt(filterRange.from), parseInt(filterRange.to)] })
          this.selectedFilters.push(this.getCustomSelectedFilter(filterValue) ?? filterObject)
        } else {
          const regular = {
            id: filterKey,
            values: [filterValue]
          }
          this.selectedFilters.push(this.getCustomSelectedFilter(filterValue) ?? regular)
        }
      }

      for (let i = 0; i < this.selectedFilters.length; i++) {
        let filterGroup = this.selectedFilters[i]
        if (
          (filterGroup?.id !== 'smartsearch' && filterGroup?.id !== 'categories' && filterGroup?.values?.length > 0) ||
          (filterGroup?.type && filterGroup?.type === 'range' && filterGroup?.values)
        ) {
          if (filterGroup.type === 'range') {
            // if range, then add object values to the url
            let groupValues = filterGroup?.values?.from + ',' + filterGroup?.values?.to
            updateQueryStringParam(filterGroup.id, groupValues)
          } else if (filterGroup.customId) {
            let groupValues = filterGroup?.values?.join('|')
            updateQueryStringParam(filterGroup.customId, groupValues)
          } else {
            let groupValues = filterGroup?.values?.join('|')
            updateQueryStringParam(filterGroup.id, groupValues)
          }
        }
      }

      updateQueryStringParam('page', 1)

      this.$emit('filterChanged', this.selectedFilters)
      this.$store.commit('products/productFilter/setSelectedFilters', this.selectedFilters)
    },

    clearFilter() {
      let baseUrl = [location.protocol, '//', location.host, location.pathname].join('')
      let queries = this.$route.query
      if (queries) {
        // delete all objects except query
        Object.keys(queries).forEach((key) => {
          if (key !== 'query') {
            delete queries[key]
          }
        })
      }

      this.$store.commit('products/productFilter/setSelectedFilters', [])
      this.filterModel = []
      this.selectedFilters = []

      window.history.replaceState(
        {},
        '',
        baseUrl +
          (Object.keys(queries).length
            ? '?' +
              Object.keys(queries)
                .map((key) => key + '=' + queries[key])
                .join('&')
            : '')
      )
      this.$emit('filterChanged', this.selectedFilters)
    },
    handleFilterSearch(filters, filterGroupName) {
      const query = this.filterSearchQueries[filterGroupName].toLowerCase()

      // include danish special characters
      const modifiedQuery = query.replace(/aa/g, 'å').replace(/ae/g, 'æ').replace(/oe/g, 'ø')

      let filteredFilters = []

      if (modifiedQuery !== '') {
        // Filter the filters based on the search query
        filters.map((filter) => filter.name.toLowerCase().includes(modifiedQuery) && filteredFilters.push(filter))

        // Update the filtered filters for the specific group
        this.$set(this.filteredFilterGroups, filterGroupName, filteredFilters)
      } else {
        // If the query is empty, remove the group from the object
        this.$delete(this.filteredFilterGroups, filterGroupName)
      }
    },
    getFilterArray(filter) {
      const filterGroupName = filter.name
      const filteredGroup = this.filteredFilterGroups[filterGroupName]
      return filteredGroup ? filteredGroup : filter.filters
    },
    getRange(filter) {
      let selectedFilter = this.selectedFilters.find((x) => x.id === filter.name)
      if (selectedFilter && (selectedFilter.values.from > filter.minValue || selectedFilter.values.to < filter.maxValue)) {
        let rangeFilter = {
          facet: filter.name,
          type: 'range',
          range: [selectedFilter.values.from, selectedFilter.values.to]
        }
        return rangeFilter
      } else {
        return this.filters.find((x) => x.name === filter.name)
      }
    },
    updateSliderValue(filterIndex, event, valueType) {
      const newValue = parseFloat(event?.target?.value)
      const filter = this.filters[filterIndex]
      const range = this.getRange(filter).range

      if (range) {
        if (valueType === 'minValue') {
          if (newValue < filter.minValue || !newValue) {
            range[0] = filter.minValue
          } else if (newValue > range[1]) {
            range[0] = range[1] - 1
          } else {
            range[0] = newValue
          }
        } else if (valueType === 'maxValue') {
          if (newValue >= filter.maxValue || !newValue) {
            range[1] = filter.maxValue
          } else if (newValue <= range[0]) {
            range[1] = range[0] + 1
          } else {
            range[1] = newValue
          }
        } else if (valueType === 'reset') {
          range[0] = filter.minValue
          range[1] = filter.maxValue
        }
      }
      // Send api request with updated filters
      this.filterChanged(filter.name, range, filter.type, filter.minValue, filter.maxValue)
    },
    selectValue(event) {
      // Select the input value when clicking inside the input field.
      event.target.select()
    },
    // gtm filters tracking
    gtmCategories(payload) {
      trackFilterCategories(payload)
    },
    gtmFilters(category, filter) {
      trackFilters(category, filter)
    }
  }
}
</script>
